import { message } from 'antd';

export type SharePayload = {
  title?: string;
  text?: string;
  url?: string;
  files?: File[];
};

type NavigatorWithShare = Navigator & {
  share?: (data: SharePayload) => Promise<void>;
  canShare?: (data: SharePayload) => boolean;
  clipboard?: Clipboard;
};

const isWindowsDesktop = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /Windows NT/i.test(ua) && !/Mobile/i.test(ua);
};

const isMobileDevice = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /Android|iPhone|iPad|iPod|Mobile/i.test(ua);
};

export const isWeChatBrowser = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /MicroMessenger/i.test(ua);
};

const getUserAgent = () => {
  if (typeof navigator === 'undefined') return '';
  return navigator.userAgent || '';
};

const isXiaoHongShuBrowser = () => {
  const ua = getUserAgent();
  return /XiaoHongShu/i.test(ua) || /\bRED\b/i.test(ua);
};

const isWeiboBrowser = () => {
  const ua = getUserAgent();
  return /\bWeibo\b/i.test(ua);
};

const isQQInAppBrowser = () => {
  const ua = getUserAgent();
  if (/MQQBrowser/i.test(ua)) return false;
  return /\bQQ\/\d+/i.test(ua) || /\bV1_AND_SQ\b/i.test(ua);
};

const isDouyinBrowser = () => {
  const ua = getUserAgent();
  return /aweme/i.test(ua) || /douyin/i.test(ua);
};

const isKuaishouBrowser = () => {
  const ua = getUserAgent();
  return /\bKwai\b/i.test(ua) || /Kuaishou/i.test(ua);
};

const getInAppBrowserName = () => {
  if (isWeChatBrowser()) return '微信';
  if (isQQInAppBrowser()) return 'QQ';
  if (isWeiboBrowser()) return '微博';
  if (isXiaoHongShuBrowser()) return '小红书';
  if (isDouyinBrowser()) return '抖音';
  if (isKuaishouBrowser()) return '快手';
  return '';
};

const isShortVideoInAppBrowser = () => {
  return isXiaoHongShuBrowser() || isDouyinBrowser() || isKuaishouBrowser();
};

const isInAppBrowser = () => {
  return isWeChatBrowser() || isQQInAppBrowser() || isWeiboBrowser() || isShortVideoInAppBrowser();
};

const copyToClipboardLegacy = (text: string): boolean => {
  if (typeof document === 'undefined') return false;
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.top = '0';
  textarea.style.left = '0';
  textarea.style.width = '1px';
  textarea.style.height = '1px';
  textarea.style.padding = '0';
  textarea.style.border = 'none';
  textarea.style.outline = 'none';
  textarea.style.boxShadow = 'none';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch (e) {
    void e;
    ok = false;
  }
  try {
    document.body.removeChild(textarea);
  } catch (e) {
    void e;
  }
  return ok;
};

const getOrigin = () => {
  if (typeof window === 'undefined') return '';
  try {
    return window.location.origin || '';
  } catch {
    return '';
  }
};

const buildBrowserOpenTip = (): string => {
  const app = getInAppBrowserName();
  if (app) {
    return `建议用浏览器打开：${app}内点右上角“…”→“在浏览器打开”`;
  }
  return '建议用浏览器打开：复制链接到浏览器地址栏打开';
};

const buildReaderUrl = (bookId: string | number, extraQuery?: Record<string, string | number | undefined>) => {
  const origin = getOrigin();
  const basePath = `/reader/${bookId}`;
  const params = Object.entries(extraQuery || {})
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&');
  const path = params ? `${basePath}?${params}` : basePath;
  return origin ? `${origin}${path}` : path;
};

const baseUrl = ((import.meta as unknown as { env?: { BASE_URL?: string } }).env?.BASE_URL || '/').replace(/\/?$/, '/');
const logoAssetPath = `${baseUrl}icons/My_logo.png`;
let logoFilePromise: Promise<File | null> | null = null;

const getLogoFile = async (): Promise<File | null> => {
  try {
    if (typeof window === 'undefined') return null;
    if (typeof File !== 'function') return null;
    const res = await fetch(logoAssetPath, { cache: 'force-cache' });
    if (!res.ok) return null;
    const blob = await res.blob();
    const type = blob.type || 'image/png';
    return new File([blob], 'bandume.png', { type });
  } catch (e) {
    void e;
    return null;
  }
};

const buildShortVideoCopyText = (payload: SharePayload) => {
  const { text, url } = payload;
  const body = String(text || '')
    .split('\n')
    .map(s => s.trim())
    .filter(Boolean)
    .join('\n')
    .replace(/\s*\|\s*/g, '\n');
  const link = String(url || '').trim();
  const lines = [body, link, '#bandume #电子书 #阅读'].filter(Boolean);
  return lines.join('\n').trim();
};

const buildWeiboCopyText = (payload: SharePayload) => {
  const { text, url } = payload;
  const body = String(text || '')
    .split('\n')
    .map(s => s.trim())
    .filter(Boolean)
    .join('\n')
    .replace(/\s*\|\s*/g, '\n');
  const link = String(url || '').trim();
  const lines = [body, link, '#bandume# #阅读#'].filter(Boolean);
  return lines.join('\n').trim();
};

const buildGenericInAppCopyText = (payload: SharePayload) => {
  const { text, url } = payload;
  const body = String(text || '')
    .split('\n')
    .map(s => s.trim())
    .filter(Boolean)
    .join('\n')
    .replace(/\s*\|\s*/g, '\n');
  const link = String(url || '').trim();
  return [body, link].filter(Boolean).join('\n').trim();
};

export const buildBookSharePayload = (options: {
  bookId: string | number;
  title?: string;
  author?: string;
  excerpt?: string;
  progress?: number;
  onlyCurrentPage?: boolean;
}): SharePayload => {
  const { bookId, title, author, excerpt, progress, onlyCurrentPage } = options;
  const safeTitle = String(title || '').trim();
  const safeAuthor = String(author || '').trim();
  const hasExcerpt = typeof excerpt === 'string' && excerpt.trim().length > 0;
  const trimmedExcerpt = hasExcerpt ? excerpt.trim().slice(0, 200) : '';
  const progressVal = typeof progress === 'number' && Number.isFinite(progress) ? Math.max(0, Math.min(100, progress)) : undefined;

  const parts: string[] = [];
  if (safeTitle) {
    parts.push(`《${safeTitle}》`);
  }
  if (safeAuthor) {
    parts.push(`作者：${safeAuthor}`);
  }
  if (onlyCurrentPage && hasExcerpt) {
    parts.push(`当前书页节选：${trimmedExcerpt}`);
  } else if (hasExcerpt) {
    parts.push(`节选：${trimmedExcerpt}`);
  }
  if (progressVal !== undefined) {
    parts.push(`阅读进度：${progressVal.toFixed(1)}%`);
  }

  const tip = buildBrowserOpenTip();
  const baseText = parts.join(' | ').trim();
  const text = baseText ? `${baseText}\n${tip}` : tip;
  const url = buildReaderUrl(bookId, progressVal !== undefined ? { progress: progressVal.toFixed(2) } : undefined);

  const titlePrefix = onlyCurrentPage ? '分享当前书页' : '分享书籍';
  const shareTitle = safeTitle ? `${titlePrefix}：《${safeTitle}》` : titlePrefix;

  return {
    title: shareTitle,
    text,
    url,
  };
};

export const buildSiteSharePayload = (options?: { siteName?: string; tagline?: string }): SharePayload => {
  const origin = getOrigin();
  const siteName = String(options?.siteName || 'bandume').trim() || 'bandume';
  const tagline = String(options?.tagline || '电子书管理与阅读平台').trim() || '电子书管理与阅读平台';
  const tip = buildBrowserOpenTip();
  const text = `${siteName}：${tagline}\n${tip}`;
  return {
    title: `${siteName} - ${tagline}`,
    text,
    url: origin || '/',
  };
};

export const shareContent = async (payload: SharePayload): Promise<void> => {
  const nav = (typeof navigator !== 'undefined' ? (navigator as NavigatorWithShare) : undefined);
  const { title, text, url } = payload;
  const combinedText = [text, url].filter(Boolean).join('\n').trim();
  const inShortVideoApp = isShortVideoInAppBrowser();
  const inWeiboApp = isWeiboBrowser();
  const inApp = isInAppBrowser();
  const copyTarget = (inShortVideoApp ? buildShortVideoCopyText(payload) : inWeiboApp ? buildWeiboCopyText(payload) : inApp ? buildGenericInAppCopyText(payload) : combinedText) || text || url || '';

  if (!copyTarget) {
    message.warning('当前无法生成可分享的内容');
    return;
  }

  const mobile = isMobileDevice();
  const windowsDesktop = isWindowsDesktop();

  if (!inApp && mobile && !windowsDesktop && nav && typeof nav.share === 'function') {
    const shareData: SharePayload = {
      title,
      text: combinedText || text || url,
      url,
    };
    try {
      if (nav.canShare && typeof nav.canShare === 'function') {
        if (!logoFilePromise) logoFilePromise = getLogoFile();
        const logoFile = await logoFilePromise;
        if (logoFile && nav.canShare({ ...shareData, files: [logoFile] })) {
          shareData.files = [logoFile];
        }
      }
    } catch (e) {
      void e;
    }
    try {
      await nav.share(shareData);
      message.success('已分享，可以在目标应用中直接查看');
      return;
    } catch (error) {
      const err = error as { name?: string };
      if (err && err.name === 'AbortError') {
        return;
      }
    }
  }

  let copied = false;
  if (nav && nav.clipboard && typeof nav.clipboard.writeText === 'function' && typeof window !== 'undefined' && (window as Window & { isSecureContext?: boolean }).isSecureContext !== false) {
    try {
      await nav.clipboard.writeText(copyTarget);
      copied = true;
    } catch (e) {
      void e;
      copied = false;
    }
  }

  if (!copied) {
    const legacyOk = copyToClipboardLegacy(copyTarget);
    if (legacyOk) {
      copied = true;
    }
  }

  if (copied) {
    if (inShortVideoApp) {
      const appName = getInAppBrowserName() || '目标平台';
      message.success(`已复制分享文案与链接，可直接粘贴到${appName}发布`);
      return;
    }
    if (inWeiboApp) {
      message.success('已复制分享文案与链接，可直接粘贴到微博发布');
      return;
    }
    if (inApp) {
      const appName = getInAppBrowserName();
      message.success(appName ? `已复制分享内容，可直接粘贴到${appName}` : '已复制分享内容，可直接粘贴到社交媒体或发送给好友');
      return;
    }
    message.success('已复制分享内容，可粘贴到社交媒体或发送给好友');
    return;
  }

  if (typeof window !== 'undefined' && typeof window.prompt === 'function') {
    try {
      window.prompt('请长按选择并复制以下内容后分享到社交媒体或发送给好友：', copyTarget);
    } catch (e) {
      void e;
    }
  } else {
    message.warning('当前浏览器不支持自动复制，请手动复制分享内容');
  }
};
